import { from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import Modal from '@roo/roo/Modal';

import { warningToast } from '@utils/toast';
import { printOrderWhenConfirm } from '@utils/print/orderPrintService';
import { getStorage, setStorage } from '@utils/localStorage';
import { isInApp, isUniversalWindowsXP, runtimeType } from '@utils/getEnv';
import { CUSTOM_METRICS, reportCustomMetrics } from '@utils/owlReporter';
import * as Ids from '@utils/ids';
import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';
import {
    OrderUnifiedInfo,
    UnprocessedOrderType,
} from '../../../../Business/pages/OrderManage/shared/types';
import { openSignProtocolModal } from '../../../../Business/pages/OrderManage/wmUnifiedOrder/components/OrderCardOperation/AcceptOrderButton';

import store from '../../../store';
import PushMessage from '../../push/PushMessage';
import { NewOrderPushData, ApplyRefundPushData, PushOrderUserRole } from '../../push/pushDataTypes';
import * as API from './api';

/** 餐饮新形态灰度 */
const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
/** 更新订单计数 */
const updateOrderCount = (
    payload: Partial<{ [key in UnprocessedOrderType]: string }> & {
        noticeInner?: boolean;
        forbiddenReload?: boolean;
    },
) => {
    store.dispatch({
        type: 'orderCount/updateCount',
        payload,
    });
};

/**
 * 收到新订单 Push 后请求订单详情信息
 * 拼好饭拼单一起送使用原订单详情接口
 */
const getOrderDetailFromPush = (message: PushMessage<NewOrderPushData>) => {
    return from(
        message.data.user_role === PushOrderUserRole.NORMAL
            ? API.getOrderDetailFromPushNew(message.data.wmOrderViewId, message.wmPoiId)
            : API.getOrderDetailFromPush(message.data.wmOrderViewId, message.wmPoiId),
    ).pipe(map(detail => (detail ? { ...detail, __originalPushData: message.data } : null)));
};

/** 获取订单菜单 */
const getOrderMenuInfo = () => from(API.getOrderMenuInfo()).pipe(catchError(() => of([])));

/** 获取全量订单计数 */
const getAllOrderCount = (updateTabs?: [any]) =>
    from(API.getOrderUnifiedCountInfo(updateTabs)).pipe(catchError(() => of(null)));

/** 获取新订单计数 */
const getNewOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.NewOrder)).pipe(
        catchError(() => of(null)),
    );

/** 请求待出餐订单数 */
const getPrepMealOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.PrepMeal)).pipe(
        catchError(() => of(null)),
    );

/** 请求待发配送订单数 */
const getPrepDeliveryOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.PrepLogistics)).pipe(
        catchError(() => of(null)),
    );

/** 获取待处理退款订单数量 */
const getRefundOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.Refund)).pipe(
        catchError(() => of(null)),
    );

/** 获取食安问题订单计数 */
const getFoodSafeProcess4PoiOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.FoodSafeProcess4Poi)).pipe(
        catchError(() => of(null)),
    );

/** 获取待处理催单订单数量 */
const getRemindOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.Reminder)).pipe(
        catchError(() => of(null)),
    );

/** 获取出餐异常订单计数 */
const getMealSlowOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.MealAbnormal)).pipe(
        catchError(() => of(null)),
    );

/** 获取配送异常订单计数 */
const getDeliveryExceptionOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.LogisticsAbnormal)).pipe(
        catchError(() => of(null)),
    );

/** 获取其它异常订单计数 */
const getOtherExceptionOrderCount = () =>
    from(API.getOrderCountInfo(API.UnprocessedOrderCountType.EtaModify)).pipe(
        catchError(() => of(null)),
    );

/** 代购商家自动退款 */
const autoAgreeRefund = (message: PushMessage<ApplyRefundPushData>) => {
    if (message.wmPoiId && message.wmPoiId !== 'unknown' && message.data.wmOrderViewId) {
        API.autoAgreeRefund(message.wmPoiId, message.data.wmOrderViewId).catch(() => null);
    }
};

const getPreOrderNearDeliveryTimeCount = () =>
    from(API.getPreOrderNearDeliveryTimeCount()).pipe(catchError(() => of(null)));

/**
 * 确认接单
 */
const confirmOrderAndPrint = (
    orderUnifiedInfo: OrderUnifiedInfo,
    operationType: 'auto_push' | 'auto_loop' | 'manual',
) => {
    const orderLogData = {
        invokeTime: new Date().getTime(),
        orderId: orderUnifiedInfo.wmOrderViewId,
        poiId: orderUnifiedInfo.wmPoiId,
        type: 0 as const,
    };
    API.confirmOrder(orderUnifiedInfo).then(
        response => {
            const responseTime = new Date().getTime();

            reportCustomMetrics(
                CUSTOM_METRICS.CONFIRM_ORDER.KEY,
                1,
                {
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.HTTP_SUCCESS]: 1,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.OPERATION_TYPE]: operationType,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.BUSINESS_CODE]: response.code,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.BUSINESS_TYPE]:
                        orderUnifiedInfo.businessType === 2 ? 'phf' : 'wm',
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_ORDER2]: 1,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.RUNTIME]: runtimeType,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_XP]: isUniversalWindowsXP ? 1 : 0,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
                },
                {
                    acctId: Ids.getAcctId(),
                    wmPoiId: Ids.getWmPoiId(),
                },
            );

            if (response.code === 0 || response.code === 10031 || response.code === 10032) {
                // 🖨 打印小票
                printOrderWhenConfirm(orderUnifiedInfo);

                // 补充协议签约提醒
                if (response.code === 10031) {
                    openSignProtocolModal('美团跑腿');
                } else if (response.code === 10032) {
                    openSignProtocolModal('混合');
                }

                // 设备维度备餐提醒，每个设备只弹三次
                const readTime = parseInt(getStorage('readTime') || '0', 10) || 0;
                if (readTime < 3) {
                    // 展示不足三次，打开弹框并计数
                    setStorage('readTime', `${readTime + 1}`);
                    Modal.confirm({
                        title: '备餐提醒',
                        showCancelButton: false,
                        confirmText: '我知道了',
                        size: 'sm',
                        children:
                            '用户有权在接单1分钟内极速退款，请接单1分钟后开始备餐，以免造成餐损',
                    });
                }
            } else if (response.code === 3001) {
                // 负订单通知 静默处理
            } else {
                warningToast(response.msg);
            }

            // 履约新形态
            // 全部订单没有计数，只用于列表刷新
            updateOrderCount({
                [UnprocessedOrderType.All]: '1',
                noticeInner: true,
            });

            const domTime = new Date().getTime();

            API.reportOrderLog({ ...orderLogData, responseTime, domTime });
        },
        () => {
            // 履约新形态
            // 全部订单没有计数，只用于列表刷新
            updateOrderCount({
                [UnprocessedOrderType.All]: '1',
                noticeInner: true,
            });
            reportCustomMetrics(
                CUSTOM_METRICS.CONFIRM_ORDER.KEY,
                1,
                {
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.HTTP_SUCCESS]: 0,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.OPERATION_TYPE]: operationType,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.BUSINESS_TYPE]:
                        orderUnifiedInfo.businessType === 2 ? 'phf' : 'wm',
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_ORDER2]: 1,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.RUNTIME]: runtimeType,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_XP]: isUniversalWindowsXP ? 1 : 0,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
                },
                {
                    acctId: Ids.getAcctId(),
                    wmPoiId: Ids.getWmPoiId(),
                },
            );
        },
    );
};

/** 唤起PC客户端或闪烁程序图标 */
const flashWindow = () => {
    if (isInApp && !!window.wmSystemApi) {
        const notifyType = getStorage('waimai_e_order_remind_type') || 'alert';

        if (notifyType === 'alert') {
            window.wmSystemApi!.showWindow();
        } else if (notifyType === 'flash') {
            // NOTE: 老壳子里会调用 wmSystemAPI.flashWindow，但是新壳子并未实现该API。
            // NOTE: 此处仅保留逻辑分支
        }
    }
};

export {
    getAllOrderCount,
    getNewOrderCount,
    getOrderDetailFromPush,
    confirmOrderAndPrint,
    flashWindow,
    updateOrderCount,
    getPrepMealOrderCount,
    getPrepDeliveryOrderCount,
    getRefundOrderCount,
    getFoodSafeProcess4PoiOrderCount,
    autoAgreeRefund,
    getPreOrderNearDeliveryTimeCount,
    getRemindOrderCount,
    getMealSlowOrderCount,
    getDeliveryExceptionOrderCount,
    getOtherExceptionOrderCount,
    getOrderMenuInfo,
};
